import {useCallback, useEffect, useRef, useState} from 'react';
import {FeedComment, socialFeedApi} from '../../../../services/socialFeedApi';
import {isRequestCanceled} from '../utils';

export type HomeCommentTarget = {
  type: 'post' | 'reel';
  id: string;
  title: string;
  subtitle?: string;
  commentCount: number;
};

interface UseHomeCommentsOptions {
  token: string | null;
  onPostCommentCountChange: (postId: string, commentCount: number) => void;
  onReelCommentCountChange: (reelId: string, commentCount: number) => void;
}

export const useHomeComments = ({
  token,
  onPostCommentCountChange,
  onReelCommentCountChange,
}: UseHomeCommentsOptions) => {
  const commentsRequestControllerRef = useRef<AbortController | null>(null);

  const [selectedTarget, setSelectedTarget] = useState<HomeCommentTarget | null>(null);
  const [comments, setComments] = useState<FeedComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentValidationError, setCommentValidationError] = useState<string | null>(null);
  const [commentSubmitError, setCommentSubmitError] = useState<string | null>(null);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [deletingCommentIds, setDeletingCommentIds] = useState<Record<string, boolean>>({});

  const syncCommentCount = useCallback((target: HomeCommentTarget, commentCount: number) => {
    const normalizedCommentCount = Math.max(0, commentCount);

    if (target.type === 'post') {
      onPostCommentCountChange(target.id, normalizedCommentCount);
    } else {
      onReelCommentCountChange(target.id, normalizedCommentCount);
    }

    setSelectedTarget((currentTarget) => (
      currentTarget && currentTarget.type === target.type && currentTarget.id === target.id
        ? {
            ...currentTarget,
            commentCount: normalizedCommentCount,
          }
        : currentTarget
    ));
  }, [onPostCommentCountChange, onReelCommentCountChange]);

  const loadComments = useCallback(async (target: HomeCommentTarget, signal?: AbortSignal) => {
    if (!token) {
      setComments([]);
      setCommentsError('You must be signed in to view comments.');
      setCommentsLoading(false);
      return;
    }

    try {
      setCommentsLoading(true);
      setCommentsError(null);

      const response = target.type === 'post'
        ? await socialFeedApi.getPostComments(token, target.id, signal)
        : await socialFeedApi.getReelComments(token, target.id, signal);

      if (signal?.aborted) {
        return;
      }

      setComments(response.comments);
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setCommentsError(error instanceof Error ? error.message : 'Failed to load comments');
    } finally {
      if (!signal?.aborted) {
        setCommentsLoading(false);
      }
    }
  }, [token]);

  const openComments = useCallback((target: HomeCommentTarget) => {
    commentsRequestControllerRef.current?.abort();
    setSelectedTarget(target);
    setComments([]);
    setCommentsLoading(true);
    setCommentsError(null);
    setCommentDraft('');
    setCommentValidationError(null);
    setCommentSubmitError(null);
    setSubmittingComment(false);
    setDeletingCommentIds({});
  }, []);

  const closeComments = useCallback(() => {
    commentsRequestControllerRef.current?.abort();
    commentsRequestControllerRef.current = null;
    setSelectedTarget(null);
    setComments([]);
    setCommentsLoading(false);
    setCommentsError(null);
    setCommentDraft('');
    setCommentValidationError(null);
    setCommentSubmitError(null);
    setSubmittingComment(false);
    setDeletingCommentIds({});
  }, []);

  const retryLoadingComments = useCallback(() => {
    if (!selectedTarget || !token) {
      return;
    }

    commentsRequestControllerRef.current?.abort();
    const controller = new AbortController();
    commentsRequestControllerRef.current = controller;
    void loadComments(selectedTarget, controller.signal);
  }, [loadComments, selectedTarget, token]);

  const submitComment = useCallback(async () => {
    if (!selectedTarget) {
      return;
    }

    if (!token) {
      setCommentSubmitError('You must be signed in to comment.');
      return;
    }

    const trimmedDraft = commentDraft.trim();

    if (!trimmedDraft) {
      setCommentValidationError('Enter a comment before posting.');
      return;
    }

    if (trimmedDraft.length > 2000) {
      setCommentValidationError('Comments must be 2000 characters or less.');
      return;
    }

    try {
      setSubmittingComment(true);
      setCommentValidationError(null);
      setCommentSubmitError(null);

      const response = selectedTarget.type === 'post'
        ? await socialFeedApi.createPostComment(token, selectedTarget.id, trimmedDraft)
        : await socialFeedApi.createReelComment(token, selectedTarget.id, trimmedDraft);

      setComments((currentComments) => [...currentComments, response.comment]);
      syncCommentCount(selectedTarget, response.commentCount);
      setCommentDraft('');
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setCommentSubmitError(error instanceof Error ? error.message : 'Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  }, [commentDraft, selectedTarget, syncCommentCount, token]);

  const deleteComment = useCallback(async (commentId: string) => {
    if (!selectedTarget || !token || deletingCommentIds[commentId]) {
      return;
    }

    try {
      setCommentSubmitError(null);
      setDeletingCommentIds((currentDeletingCommentIds) => ({
        ...currentDeletingCommentIds,
        [commentId]: true,
      }));

      const response = selectedTarget.type === 'post'
        ? await socialFeedApi.deletePostComment(token, commentId)
        : await socialFeedApi.deleteReelComment(token, commentId);

      setComments((currentComments) => currentComments.filter((comment) => comment.id !== commentId));
      syncCommentCount(selectedTarget, response.commentCount);
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setCommentSubmitError(error instanceof Error ? error.message : 'Failed to delete comment');
    } finally {
      setDeletingCommentIds((currentDeletingCommentIds) => {
        const nextDeletingCommentIds = {...currentDeletingCommentIds};
        delete nextDeletingCommentIds[commentId];
        return nextDeletingCommentIds;
      });
    }
  }, [deletingCommentIds, selectedTarget, syncCommentCount, token]);

  useEffect(() => {
    if (!selectedTarget) {
      commentsRequestControllerRef.current?.abort();
      commentsRequestControllerRef.current = null;
      return;
    }

    const controller = new AbortController();
    commentsRequestControllerRef.current?.abort();
    commentsRequestControllerRef.current = controller;
    void loadComments(selectedTarget, controller.signal);

    return () => {
      controller.abort();
      if (commentsRequestControllerRef.current === controller) {
        commentsRequestControllerRef.current = null;
      }
    };
  }, [loadComments, selectedTarget]);

  useEffect(() => {
    if (!token) {
      closeComments();
    }
  }, [closeComments, token]);

  return {
    selectedTarget,
    isCommentsModalOpen: Boolean(selectedTarget),
    comments,
    commentsLoading,
    commentsError,
    commentDraft,
    commentValidationError,
    commentSubmitError,
    submittingComment,
    deletingCommentIds,
    setCommentDraft,
    openComments,
    closeComments,
    retryLoadingComments,
    submitComment,
    deleteComment,
  };
};
